import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, Loader2, Eye, FileSpreadsheet } from 'lucide-react';
import { formatIDR, formatDateID } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function Payments() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await api.get('/payments', { params });
      setList(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const total = list.reduce((a, b) => a + (b.amount || 0), 0);

  const exportCsv = () => {
    const rows = [['Nomor', 'Tanggal', 'Client', 'Invoice', 'Metode', 'Referensi', 'Jumlah']];
    list.forEach(p => {
      rows.push([p.payment_number, formatDateID(p.payment_date), p.client_name || '', p.invoice_number || '', p.payment_method || '', p.payment_reference_number || '', p.amount || 0]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `pembayaran-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV diekspor');
  };

  return (
    <div className="space-y-4" data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="payments-search-input" placeholder="Cari nomor / referensi…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">Total: <span className="font-mono-num text-foreground font-semibold">{formatIDR(total)}</span></div>
          <Button variant="secondary" onClick={exportCsv} data-testid="payments-export-csv"><FileSpreadsheet className="h-4 w-4 mr-2" />Ekspor CSV</Button>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="payments-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>No</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Metode</TableHead>
                  <TableHead>Referensi</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</TableCell></TableRow>}
                {!loading && list.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Belum ada pembayaran.</TableCell></TableRow>}
                {list.map(p => (
                  <TableRow key={p.id} className="hover:bg-white/4 border-border">
                    <TableCell className="font-mono-num text-xs">{p.payment_number}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDateID(p.payment_date)}</TableCell>
                    <TableCell className="font-medium">{p.client_name}</TableCell>
                    <TableCell className="font-mono-num text-xs">{p.invoice_number}</TableCell>
                    <TableCell>{p.payment_method}</TableCell>
                    <TableCell className="text-muted-foreground">{p.payment_reference_number || '-'}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(p.amount)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/invoices/${p.invoice_id}`)}><Eye className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
